import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { loginRequest } from '../auth/authConfig'
import StarField from '../components/StarField'
import BreezyLogo from '../components/BreezyLogo'

export default function Login() {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const navigate = useNavigate()

  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, inProgress, navigate])

  const handleLogin = () => {
    instance.loginRedirect(loginRequest)
  }

  const isLoading = inProgress !== InteractionStatus.None

  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080c18' }}>
        <StarField />
        <div className="text-center relative z-10">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)'
            }}>
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <p className="text-blue-300/70 font-medium tracking-wide">Authenticating...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 relative" style={{ background: '#080c18' }}>
      <StarField />

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-lg">
        {/* Logo - Big and centered */}
        <div className="flex justify-center mb-12">
          <BreezyLogo size="xl" />
        </div>

        {/* Login Card - Frosted Glass */}
        <div
          className="rounded-3xl p-8 mb-8"
          style={{
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(100, 180, 255, 0.15)',
            boxShadow: '0 0 60px rgba(59, 130, 246, 0.1), inset 0 1px 1px rgba(255,255,255,0.05)'
          }}
        >
          {/* Sign In Button */}
          <button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full group relative overflow-hidden rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
              border: '1px solid rgba(96, 165, 250, 0.4)',
              padding: '18px 24px'
            }}
          >
            {/* Button glow on hover */}
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)'
              }}
            />

            <span className="relative z-10 flex items-center justify-center gap-3">
              {isLoading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin text-xl" style={{ color: '#60a5fa' }}></i>
                  <span className="text-lg" style={{ color: '#93c5fd' }}>Signing in...</span>
                </>
              ) : (
                <>
                  <i className="fa-brands fa-microsoft text-2xl" style={{ color: '#60a5fa' }}></i>
                  <span className="text-lg" style={{ color: '#e2e8f0' }}>Sign in with Microsoft</span>
                </>
              )}
            </span>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(100, 180, 255, 0.3), transparent)' }}></div>
            <span className="text-xs font-medium tracking-widest uppercase" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              Enterprise SSO
            </span>
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(100, 180, 255, 0.3), transparent)' }}></div>
          </div>

          {/* Status Indicator */}
          <div className="flex items-center justify-center gap-2">
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#22c55e', boxShadow: '0 0 10px #22c55e' }}></span>
            <span className="text-xs font-medium" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>All systems operational</span>
          </div>
        </div>

        {/* Features - Frosted Glass Cards */}
        <div className="space-y-4">
          {/* Feature 1 */}
          <div
            className="rounded-2xl p-5 flex items-start gap-4 group transition-all duration-300 hover:scale-[1.02]"
            style={{
              background: 'rgba(15, 23, 42, 0.4)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(100, 180, 255, 0.1)'
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(6, 182, 212, 0.2) 100%)',
                border: '1px solid rgba(59, 130, 246, 0.3)'
              }}
            >
              <i className="fa-solid fa-microphone text-lg" style={{ color: '#60a5fa' }}></i>
            </div>
            <div>
              <h3 className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>AI-Powered Transcription</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                Convert workshop recordings into accurate, searchable transcripts using Azure Speech Services.
              </p>
            </div>
          </div>

          {/* Feature 2 */}
          <div
            className="rounded-2xl p-5 flex items-start gap-4 group transition-all duration-300 hover:scale-[1.02]"
            style={{
              background: 'rgba(15, 23, 42, 0.4)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(139, 92, 246, 0.1)'
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)',
                border: '1px solid rgba(139, 92, 246, 0.3)'
              }}
            >
              <i className="fa-solid fa-robot text-lg" style={{ color: '#a78bfa' }}></i>
            </div>
            <div>
              <h3 className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>Intelligent Document Generation</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                Generate comprehensive documentation automatically from your workshop insights.
              </p>
            </div>
          </div>

          {/* Feature 3 */}
          <div
            className="rounded-2xl p-5 flex items-start gap-4 group transition-all duration-300 hover:scale-[1.02]"
            style={{
              background: 'rgba(15, 23, 42, 0.4)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(6, 182, 212, 0.1)'
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(34, 211, 238, 0.2) 100%)',
                border: '1px solid rgba(6, 182, 212, 0.3)'
              }}
            >
              <i className="fa-solid fa-comments text-lg" style={{ color: '#22d3ee' }}></i>
            </div>
            <div>
              <h3 className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>Natural Language Queries</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                Ask questions about your workshop content and get AI-powered answers with source references.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
            &copy; 2026 Coretek Services
          </p>
        </div>
      </div>
    </div>
  )
}
